import { useEffect, useState, useRef } from "react";

// Module-level cache for previews
const previewCache = {};

export default function useTrackPreviews(tracks) {
  const [previewMap, setPreviewMap] = useState(() => {
    const initial = {};
    tracks?.forEach((track) => {
      const key = `${track.artist_name}-${track.song_name}`;
      // Pre-fill from cache or existing track preview
      if (previewCache[key] !== undefined) {
        initial[key] = previewCache[key];
      } else if (track.track_preview) {
        initial[key] = track.track_preview;
        previewCache[key] = track.track_preview;
      }
    });
    return initial;
  });

  // Ref to track which tracks have been requested during this mount
  const requestedRef = useRef(new Set());

  useEffect(() => {
    if (!tracks || tracks.length === 0) return;

    tracks.forEach((track) => {
      const key = `${track.artist_name}-${track.song_name}`;

      // Skip if:
      // 1. Already cached
      // 2. Already requested during this mount
      // 3. Track already has a preview
      if (
        previewCache[key] !== undefined ||
        requestedRef.current.has(key) ||
        track.track_preview
      ) {
        return;
      }

      requestedRef.current.add(key);

      fetch(
        `/api/preview?artist=${encodeURIComponent(track.artist_name)}&track=${encodeURIComponent(track.song_name)}`,
      )
        .then((res) => (res.ok ? res.json().catch(() => null) : null))
        .then((data) => {
          const previewUrl = data?.track_preview ?? "";

          // Store in module cache
          previewCache[key] = previewUrl;

          // Update React state progressively
          setPreviewMap((prev) => ({ ...prev, [key]: previewUrl }));
        })
        .catch(() => {
          previewCache[key] = "";
          setPreviewMap((prev) => ({ ...prev, [key]: "" }));
        });
    });
  }, [tracks]); // Only re-run when `tracks` changes

  if (!tracks || tracks.length === 0) {
    return [];
  }

  // Return tracks with preview attached, preferring state then cache
  return tracks.map((track) => {
    const key = `${track.artist_name}-${track.song_name}`;
    return {
      ...track,
      track_preview:
        previewMap[key] ?? track.track_preview ?? previewCache[key] ?? "",
    };
  });
}
